import React from "react";
import { useState, useEffect, useRef } from "react";
import { Link, Routes, Route } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import Workshops from "./pages/Workshops";
import Maerkte from "./pages/Maerkte";
import Galerie from "./pages/Galerie";
import Uebermich from "./pages/Uebermich";
import Kontakt from "./pages/Kontakt";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
//import Testapi from "./pages/Testapi";
//import Testmailapi from "./pages/Testmailapi";
import logo from "./assets/sibilleFrengerProfile.jpg"; // passe den Dateinamen an
//import logo from "./assets/logoDieWerkstatt.png"; // passe den Dateinamen an
import backgroundImg from "./assets/home_FrengerSibille2_hell.webp";
import "./css/Mobile.css"; // Mobile-Responsivität


function Nav() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  // Schließe Menü wenn außerhalb geklickt wird
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Schließe Menü wenn Link geklickt wird
  const handleLinkClick = () => {
    setIsOpen(false);
  };

  return (
    <nav className="nav">
      <div className="container nav__inner">
        <Link to="/" className="brand">
          <img src={logo} alt="Sibille Frenger Logo" className="brand__logo" />
          <span className="brand__text">Die Werkstatt</span>
        </Link>

        {/* Mobile Toggle Button */}
        <button
          ref={toggleRef}
          className="nav__toggle"
          type="button"
          aria-label="Menü öffnen"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="nav__toggle-bar" />
          <span className="nav__toggle-bar" />
          <span className="nav__toggle-bar" />
        </button>

        <ul
          ref={menuRef}
          className={`nav__links ${isOpen ? "nav__links--open" : ""}`}
        >
          <li className="nav__item">
            <Link to="/workshops" onClick={handleLinkClick}>Workshops</Link>
          </li>
          <li className="nav__item">
            <Link to="/maerkte" onClick={handleLinkClick}>Märkte</Link>
          </li>
          <li className="nav__item">
            <Link to="/galerie" onClick={handleLinkClick}>Galerie</Link>
          </li>
          <li className="nav__item">
            <Link to="/uebermich" onClick={handleLinkClick}>Über mich</Link>
          </li>
          <li className="nav__item">
            <Link to="/kontakt" onClick={handleLinkClick}>Kontakt</Link>
          </li>
        </ul>
      </div>
    </nav>
  );
}


function Hero() {
  return (
    <header className="hero" style={{ backgroundImage: `url(${backgroundImg})` }}>
      <div className="hero__content container">
        <div className="hero__inner">
          <h1>Herzlich Willkommen</h1>
          <p className="lead">
            Du bist auf der Suche nach kreativer und einzigartiger Floristik? 
            Herzlich Willkommen!
            Eine Übersicht aller bevorstehenden Märkte und Workshops findest du in den Übersichten. 
            Du suchst Inspiration für deine nächste Veranstaltung?
            Ob Kommunion, Geburtstag, Hochzeit, Firmenfest oder auch in traurigen Stunden, ich berate dich gerne.
          </p>
          <p className="lead">Kontaktiere mich!</p>
          <div className="cta">
            <Link to="/workshops" className="btn">Workshops</Link>
            <Link to="/maerkte" className="btn">Märkte</Link>
            <Link to="/galerie" className="btn">Galerie</Link>
            <a href="#features" className="btn btn--ghost">Weiteres</a>
          </div>
        </div>
      </div>
    </header>
  );
}

function Features() {
  const items = [
    { title: "Kreativ", text: "Meine Arbeit zeichnet sich durch fantasievolle und innovative Kreationen aus, für jeden Anlass geeignet." },
    { title: "Einzigartig", text: "Jedes Gesteck fertige ich in Handarbeit nach Deinen Wünschen individuell an." },
    { title: "Persönlich", text: "In einem Workshop, auf einem Markt oder in einem persönlichen Gespräch ich kümmere mich persönlich um Dich. Kontaktiere mich gerne." },
  ];
  return (
    <section id="features" className="section">
      <div className="container">
        <h2>Individuell und Persönlich</h2>
        <div className="grid">
          {items.map((it) => (
            <div className="card" key={it.title}>
              <h3>{it.title}</h3>
              <p>{it.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer bg-gray-900 text-white py-6 mt-12" id="kontakt">
      <div className="container footer__inner flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Copyright */}
        <p className="text-sm text-gray-400 text-center md:text-left">
          © {new Date().getFullYear()} Sibille Frenger – Die Werkstatt
        </p>

        {/* Navigation */}
        <ul className="footer__links flex flex-wrap items-center justify-center gap-4 text-sm">
          <li className="footer__item">
            <Link to="/datenschutz" className="hover:text-pink-300 transition-colors">
              Datenschutz
            </Link>
          </li>
          <li className="footer__item">
            <Link to="/impressum" className="hover:text-pink-300 transition-colors">
              Impressum
            </Link>
          </li>
          <li className="footer__item">
            <Link to="/kontakt" className="hover:text-pink-300 transition-colors">
              Kontakt
            </Link>
          </li>
        </ul>

        {/* Social Media */}
        <div className="flex items-center gap-4">
          <a
            href="https://www.instagram.com/sibillefrenger/?hl=de"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="hover:text-pink-400 transition-transform transform hover:scale-110"
          >
            <Instagram size={22} />
          </a>
          <a
            href="https://www.facebook.com/sibille.frenger"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="hover:text-blue-400 transition-transform transform hover:scale-110"
          >
            <Facebook size={22} />
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <>
      <Nav />
      <Routes>
        <Route path="/" element={<><Hero /><Features /></>} />
        <Route path="/workshops" element={<Workshops />} />
        <Route path="/maerkte" element={<Maerkte />} />
        <Route path="/galerie" element={<Galerie />} />
        <Route path="/uebermich" element={<Uebermich />} />
        <Route path="/kontakt" element={<Kontakt />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
        <Route path="/impressum" element={<Impressum />} />
      </Routes>
      <Footer />
    </>
  );
}
